import { useState, useCallback, useRef } from 'react';
import { translateText } from '@/api/translate/translateText';

interface TranslationState {
  isTranslating: boolean;
  abortController: AbortController | null;
}

export const useFieldTranslation = () => {
  const [translatingFields, setTranslatingFields] = useState<Map<string, TranslationState>>(
    new Map()
  );
  const translatingFieldsRef = useRef(translatingFields);

  translatingFieldsRef.current = translatingFields;

  const isFieldTranslating = useCallback((fieldKey: string): boolean => {
    return translatingFieldsRef.current.get(fieldKey)?.isTranslating || false;
  }, []);

  const translateField = useCallback(
    async (
      fieldKey: string,
      enValue: string,
      frFieldName: string,
      setFieldValue: (field: string, value: any) => void
    ) => {
      if (!enValue || !enValue.trim()) {
        alert('Cannot translate empty field');
        return;
      }

      const abortController = new AbortController();

      setTranslatingFields(prev => {
        const newMap = new Map(prev);
        newMap.set(fieldKey, { isTranslating: true, abortController });
        return newMap;
      });

      try {
        const result = await translateText({
          text: enValue,
          from: 'en',
          to: 'es-mx',
        });

        if (!abortController.signal.aborted) {
          setFieldValue(frFieldName, result.translation);
        }
      } catch (error: any) {
        if (!abortController.signal.aborted) {
          const errorMessage = error?.message || 'Translation failed. Please try again.';
          alert(errorMessage);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setTranslatingFields(prev => {
            const newMap = new Map(prev);
            newMap.delete(fieldKey);
            return newMap;
          });
        }
      }
    },
    []
  );

  const cancelFieldTranslation = useCallback((fieldKey: string) => {
    const state = translatingFieldsRef.current.get(fieldKey);
    if (state?.abortController) {
      state.abortController.abort();
      setTranslatingFields(prev => {
        const newMap = new Map(prev);
        newMap.delete(fieldKey);
        return newMap;
      });
    }
  }, []);

  return {
    translateField,
    isFieldTranslating,
    cancelFieldTranslation,
  };
};
